import React, { useMemo } from 'react';
import styled from 'styled-components';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';

import {
	ParentNotificationOptions,
	TestNotification,
} from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/CourseContent/Types/Test/Test.types';
import { Box, FormControlLabel, Tooltip } from '@mui/material';
import { FlexLayout, Spacer } from '@Styled/utilities';
import { useForm, Controller } from 'react-hook-form';
import { useAppThunkDispatch, useTypedSelector } from '@Features/store';
import {
	testsGetSingle,
} from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/CourseContent/Pages/Test/Slice/TestSlice';
import { useSnackbar } from '@Providers/useSnackbar';
import { useRouteProvider } from '@Providers/Routes/RoutesProvider';
import { testNotificationUpdate } from '../../Slice/TestNotifaction/TestNotifaction.slice';
import usePlatformMode from '@Hooks/usePlatformMode';
import { useIsStandaloneLecture } from '@ContentManagement/ContentModeContext';

type OptionParentNotification = {
	id: number;
	label: string;
	value: ParentNotificationOptions;
	/** Channel ready on this platform; when false the checkbox is greyed out. */
	enabled: boolean;
};

const TestNotifications: React.FC = () => {
	const { currentEntity } = useTypedSelector((state) => state.Tests);

	const defaultValues = useMemo<TestNotification | undefined>(() => {
		if (currentEntity) {
			const dv: TestNotification = {
				id: currentEntity?.id,
				// Stored options may still contain the retired 'email' channel;
				// dropping it here keeps the checkboxes consistent and lets the
				// next save scrub it from the test.
				student_notification_options:
					currentEntity?.student_notification_options?.filter(
						(option) => option !== 'email'
					),
				parent_notification_options: currentEntity?.parent_notification_options,
			};
			return dv;
		}
	}, []);

	const { control, handleSubmit, setValue, watch, formState } = useForm<TestNotification>({
		mode: 'all',
		defaultValues: defaultValues,
	});

	const watchParentOptions = watch('parent_notification_options');

	const dispatch = useAppThunkDispatch();
	const { displaySnackbar } = useSnackbar();
	const methods = useRouteProvider();
	// The instructor portal offers PARENT WhatsApp only (user decision):
	// the student section is not rendered at all, and SMS is shown dimmed
	// but can never be ticked. Anything the admin stored (student options,
	// parent SMS) is preserved untouched — the explicit save payload echoes
	// the stored form values back.
	const { whatsappEnabled } = usePlatformMode();
	// Standalone lectures are view-only in the instructor portal (the server
	// 403s authoring writes); show the configuration without a way to save it.
	const readOnly = useIsStandaloneLecture();

	const parentConnectionMethods: Array<OptionParentNotification> = useMemo(
		() => [
			// Shown but never selectable here — instructors may only toggle
			// parent WhatsApp.
			{ id: 1, label: 'SMS', value: 'sms', enabled: false },
			{ id: 2, label: 'WhatsApp', value: 'whatsapp', enabled: whatsappEnabled },
		],
		[whatsappEnabled]
	);

	const handleSelectAllParentMethods = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const locked = (watchParentOptions ?? []).filter((value) =>
			parentConnectionMethods.some(
				(method) => method.value === value && !method.enabled
			)
		);
		if (event.target.checked)
			setValue(
				'parent_notification_options',
				[
					...locked,
					...parentConnectionMethods
						.filter((method) => method.enabled)
						.map((method) => method.value),
				],
				{ shouldDirty: true }
			);
		else setValue('parent_notification_options', locked, { shouldDirty: true });
	};
	const handelSingleParentMethod = (
		event: React.ChangeEvent<HTMLInputElement>,
		value: ParentNotificationOptions
	) => {
		const temp: Array<ParentNotificationOptions> =
			new Array<ParentNotificationOptions>();
		if (event.target.checked) {
			if (watchParentOptions) {
				for (let i = 0; i < watchParentOptions.length; i++)
					temp.push(watchParentOptions[i]);
			}
			temp.push(value);
		} else {
			if (watchParentOptions) {
				for (let i = 0; i < watchParentOptions.length; i++)
					if (watchParentOptions[i] !== value) temp.push(watchParentOptions[i]);
			}
		}
		setValue('parent_notification_options', temp, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};
	// The select-all checkbox reflects the enabled channels only — disabled
	// leftovers neither block the checked state nor force indeterminate.
	const enabledParentMethods = parentConnectionMethods.filter(
		(method) => method.enabled
	);
	const checkedParentCount = enabledParentMethods.filter((method) =>
		watchParentOptions?.includes(method.value)
	).length;

	const onSave = async (form: TestNotification) => {
		try {
			// Explicit payload: include_previous_attempts is backend-only now
			// (API default false) and must not ride along from form state.
			await dispatch(
				testNotificationUpdate({
					id: form.id,
					student_notification_options: form.student_notification_options,
					parent_notification_options: form.parent_notification_options,
				})
			).unwrap();

			if (form.id) dispatch(testsGetSingle({ id: Number(form.id) }));
			displaySnackbar('success', 'Updated Test Notifications successfully');
		} catch (error) {
			displaySnackbar('error', "Active test can't be updated");
		}
	};

	return (
		<StyledContainer>
			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				<Controller
					control={control}
					name={'parent_notification_options'}
					render={({ field }) => {
						return (
							<FormControlLabel
								label="Parent"
								{...field}
								control={
									<Checkbox
										checked={
											enabledParentMethods.length > 0 &&
											checkedParentCount === enabledParentMethods.length
										}
										disabled={enabledParentMethods.length === 0}
										onChange={handleSelectAllParentMethods}
										indeterminate={
											checkedParentCount > 0 &&
											checkedParentCount < enabledParentMethods.length
										}
									/>
								}
							/>
						);
					}}
				/>
			</Box>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					marginLeft: '30px',
					marginBottom: '30px',
				}}
			>
				{parentConnectionMethods.map((method) => (
					<Tooltip
						key={method.id}
						title={
							method.enabled
								? ''
								: method.value === 'sms'
								? 'Instructors can send WhatsApp only'
								: 'Not configured for this platform — see Settings'
						}
						placement="right"
					>
						<span>
							<FormControlLabel
								label={method.label}
								disabled={!method.enabled}
								control={
									<Checkbox
										checked={
											watchParentOptions
												? watchParentOptions.includes(method.value)
												: false
										}
										onChange={(e) => {
											handelSingleParentMethod(e, method.value);
										}}
									/>
								}
							/>
						</span>
					</Tooltip>
				))}
			</Box>

			<FlexLayout justifyContent="flex-end">
				<Button
					variant="contained"
					color="warning"
					disabled={!formState.isDirty}
					onClick={() => {
						methods?.goToParent();
					}}
				>
					Cancel
				</Button>
				<Spacer mx="0.5rem" />
				<Button
					variant="contained"
					disabled={readOnly || !formState.isDirty || !formState.isValid}
					onClick={handleSubmit(onSave)}
				>
					Save
				</Button>
			</FlexLayout>
			 
		</StyledContainer>
	);
};
const StyledContainer = styled.form`
	margin-left: 2.625rem;
	align-items: center;
`;
export default TestNotifications;
